import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  Dimensions,
  Image,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useSelector, useDispatch } from "react-redux";
import { useToast } from "../../components/LhToastContainer";
import LHLoader from "../../components/LHLoader";
import { RootState, AppDispatch } from "../../store/index";
import { formatUserName } from "../../utils/emailTouserName";
import SingleSelectDropdown from "./components/SingleSelectDropdown";
import { SHIPMENT_CARRIERS } from "./util/shipmentCarrier";
import useTicketingAccess from "./hooks/useTicketingAccess";
import {
  fetchDevices,
  getDeviceById,
  createDevice,
  updateDevice,
  updateDeviceStatus,
  uploadDevicePhoto,
} from "../../api/deviceManagementApi";
import styles from "./SparePoolScreen.styles";
import mobileStyles from "./SparePoolScreen.mobile.style";
import { fetchSparePoolUnits, fetchSparePoolSummary, updateSparePoolUnitStatus } from "../../store/thunks/sparePoolThunks";
import { PoolUnitContent } from "../../api/sparePoolApi";

type Tab = "pool" | "device" | "backfill" | "report";
type PoolStatus = "in-pool" | "reserved" | "out-transit" | "deployed" | "in-transit" | "in-repair" | "retired";
type CaptureMode = "Serial per unit" | "Quantity only";
type BackfillStatus = "sustain-review" | "site-accessories" | "sustain-accessory-review" | "vendor-fulfillment" | "shipped";

type PoolHistoryEvent = {
  date: string;
  event: string;
  detail: string;
  actor: string;
  ticket?: string;
};

type PoolUnit = {
  id: string;
  vendor: string;
  sku: string;
  description: string;
  type: string;
  serial: string;
  quantity: number;
  location: string;
  ticket: string;
  sentOut: string;
  received: string;
  status: PoolStatus;
  history: PoolHistoryEvent[];
};

type Device = {
  id: string;
  sku: string;
  description: string;
  type: string;
  generation: string;
  vendor: string;
  capture: string;
  prefix: string;
  notes: string;
  inPool: number;
  status: string;
  productPhoto?: string | null;
  sourcePlatform?: string;
  eligibleRma?: boolean;
  eligibleNonRma?: boolean;
  eligibleReclaim?: boolean;
};

type BackfillTicket = {
  id: string;
  location: string;
  requestedSku: string;
  requestedDevice: string;
  fulfillmentSku: string;
  fulfillmentDevice: string;
  available: number;
  status: BackfillStatus;
  accessories: string[];
  serials: string[];
  carrier: string;
  tracking: string;
};

type ReconciliationRow = {
  site: string;
  ticket: string;
  process: string;
  expected: number;
  received: number;
  damaged: number;
  status: string;
};

type Props = { onNavigate?: (screen: string) => void };

type DeviceDraft = {
  sku: string;
  description: string;
  type: string;
  generation: string;
  vendor: string;
  capture: string;
  prefix: string;
  notes: string;
  active: boolean;
  eligibleRma: boolean;
  eligibleNonRma: boolean;
  eligibleReclaim: boolean;
  productPhoto?: string | null;
};

const WES_EQUIPMENT_TYPE = "WES Device";

// Static equipment types configuration
const EQUIPMENT_TYPES = [
  "Accessory",
  "Scanner Accessory",
  "Headset Accessory",
  "Talkman",
  "Battery",
  "Charging Accessory",
  "WES Device",
  "Headset",
  "Scanner",
];

const initialPoolUnits: PoolUnit[] = [
  { id: "pool-1", vendor: "Peak Technologies", sku: "LBDJ901291", description: "Talkman A720x wearable computer", type: "RF Device", serial: "TKA720-004182", quantity: 1, location: "Fullerton DC", ticket: "NTI-2026-00421", sentOut: "07/19/2026", received: "07/23/2026", status: "deployed", history: [{ date: "07/23/2026", event: "Received at site", detail: "Confirmed in service at Fullerton DC", actor: "Warehouse Support", ticket: "NTI-2026-00421" }] },
  { id: "pool-2", vendor: "Peak Technologies", sku: "LBDJ901291", description: "Talkman A720x wearable computer", type: "RF Device", serial: "TKA720-004183", quantity: 1, location: "Peak Technologies", ticket: "", sentOut: "", received: "09/25/2026", status: "in-pool", history: [{ date: "09/25/2026", event: "Added to spare pool", detail: "Passed QC and released as available stock", actor: "Maria Chen" }] },
  { id: "pool-3", vendor: "Dematic", sku: "LBDJ901291", description: "Talkman A720x wearable computer", type: "RF Device", serial: "SCN-882104", quantity: 1, location: "In transit to Dematic Contract Repair", ticket: "NTI-2026-00387", sentOut: "09/28/2026", received: "", status: "in-transit", history: [{ date: "09/28/2026", event: "Shipped to vendor", detail: "Return shipment awaiting vendor receipt", actor: "Maria Chen", ticket: "NTI-2026-00387" }] },
  { id: "pool-4", vendor: "Peak Technologies", sku: "SRX3 ELECTRONICS MODULE", description: "SRX3 replacement electronics module", type: "Voice Headset", serial: "SRX3-019441", quantity: 1, location: "Peak Technologies", ticket: "", sentOut: "", received: "08/12/2026", status: "in-pool", history: [{ date: "08/12/2026", event: "Added to spare pool", detail: "Counted and released as available stock", actor: "Maria Chen" }] },
  { id: "pool-5", vendor: "Peak Technologies", sku: "LBDJ901286", description: "Talkman battery", type: "Accessory", serial: "", quantity: 12, location: "Peak Technologies", ticket: "", sentOut: "", received: "06/02/2026", status: "in-pool", history: [{ date: "06/02/2026", event: "Counted into spare pool", detail: "Quantity tracked; serial numbers are not captured for this class", actor: "Maria Chen" }] },
];

const poolStatusLabels: Record<PoolStatus, string> = {
  "in-pool": "In Pool",
  reserved: "Reserved",
  "out-transit": "In Transit to Site",
  deployed: "Deployed",
  "in-transit": "Inbound to Vendor",
  "in-repair": "In Repair",
  retired: "Retired",
};

const initialDevices: Device[] = [
  { id: "device-1", sku: "TALKMAN A720", description: "Talkman A720 wearable computer", type: "RF Device", generation: "A", vendor: "Peak Technologies", capture: "Serial per unit", prefix: "TKA", notes: "", inPool: 34, status: "Active" },
  { id: "device-2", sku: "TALKMAN A500", description: "Talkman A500 wearable computer", type: "RF Device", generation: "A", vendor: "Peak Technologies", capture: "Serial per unit", prefix: "TKA", notes: "", inPool: 6, status: "Active" },
  { id: "device-3", sku: "SRX3 ELECTRONICS MODULE", description: "SRX3 replacement electronics module", type: "Voice Headset", generation: "3", vendor: "Dematic", capture: "Serial per unit", prefix: "SRX3", notes: "", inPool: 61, status: "Active" },
  { id: "device-4", sku: "SCANNERS", description: "Handheld barcode scanner", type: "Scanner", generation: "-", vendor: "Dematic", capture: "Quantity only", prefix: "", notes: "Quantity is tracked without serial numbers.", inPool: 48, status: "Inactive" },
];

const initialBackfillTickets: BackfillTicket[] = [
  { id: "NTI-2026-00446", location: "Carroll IA", requestedSku: "LBDJ901168", requestedDevice: "TALKMAN A500", fulfillmentSku: "LBDJ901291", fulfillmentDevice: "TALKMAN A720", available: 34, status: "site-accessories", accessories: [], serials: [], carrier: "", tracking: "" },
  { id: "NTI-2026-00453", location: "Fullerton DC", requestedSku: "LBDJ901291", requestedDevice: "TALKMAN A720", fulfillmentSku: "LBDJ901291", fulfillmentDevice: "TALKMAN A720", available: 34, status: "sustain-review", accessories: [], serials: [], carrier: "", tracking: "" },
];

const reconciliationRows: ReconciliationRow[] = [
  { site: "Fullerton DC", ticket: "NTI-2026-00421", process: "RMA Repair or Replacement", expected: 4, received: 4, damaged: 0, status: "Closed" },
  { site: "Carroll IA", ticket: "NTI-2026-00445", process: "Spare Pool Backfill", expected: 12, received: 11, damaged: 2, status: "Variance Open" },
  { site: "Joliet IL", ticket: "NTI-2026-00387", process: "RMA Repair or Replacement", expected: 5, received: 5, damaged: 0, status: "Closed" },
];

const unique = (values: string[]) => Array.from(new Set(values));

const deviceDraftKey = (draft: DeviceDraft) => JSON.stringify(draft);

/**
 * Convert backend status values to frontend PoolStatus type
 * Backend returns: "In Pool", "In Transit to Site", "Inbound to Vendor", etc.
 * Frontend expects: "in-pool", "in-transit", "in-repair", etc.
 */
const mapBackendStatusToPoolStatus = (backendStatus: string | undefined): PoolStatus => {
  const statusMap: Record<string, PoolStatus> = {
    "In Pool": "in-pool",
    "Reserved": "reserved",
    "In Transit to Site": "out-transit",
    "Inbound to Vendor": "in-transit",
    "Deployed": "deployed",
    "In Repair": "in-repair",
    "Retired": "retired",
  };
  return statusMap[backendStatus || ""] || "in-pool";
};

// ── Skeleton components ───────────────────────────────────────────────────────

const SkeletonBlock: React.FC<{
  pulse: Animated.Value;
  height: number;
  width?: number | string;
  radius?: number;
  style?: any;
}> = ({ pulse, height, width = "100%", radius = 6, style }) => (
  <Animated.View
    style={[
      {
        height,
        width,
        borderRadius: radius,
        backgroundColor: "#E5E7EB",
        opacity: pulse,
      },
      style,
    ]}
  />
);

const SkeletonPoolTable: React.FC<{ pulse: Animated.Value }> = ({ pulse }) => (
  <View style={styles.tableCard}>
    <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.tableScroll}>
      <View>
        {/* Header row */}
        <View style={[styles.tableRow, styles.tableHeader]}>
          <SkeletonBlock pulse={pulse} height={12} width={80} />
          <SkeletonBlock pulse={pulse} height={12} width={100} style={{ marginLeft: 16 }} />
          <SkeletonBlock pulse={pulse} height={12} width={120} style={{ marginLeft: 16 }} />
          <SkeletonBlock pulse={pulse} height={12} width={80} style={{ marginLeft: 16 }} />
          <SkeletonBlock pulse={pulse} height={12} width={60} style={{ marginLeft: 16 }} />
          <SkeletonBlock pulse={pulse} height={12} width={80} style={{ marginLeft: 16 }} />
        </View>

        {/* Skeleton rows */}
        {Array.from({ length: 8 }).map((_, rowIdx) => (
          <View key={rowIdx} style={styles.tableRow}>
            <SkeletonBlock pulse={pulse} height={12} width={80} />
            <SkeletonBlock pulse={pulse} height={12} width={100} style={{ marginLeft: 16 }} />
            <SkeletonBlock pulse={pulse} height={12} width={120} style={{ marginLeft: 16 }} />
            <SkeletonBlock pulse={pulse} height={12} width={80} style={{ marginLeft: 16 }} />
            <SkeletonBlock pulse={pulse} height={12} width={60} style={{ marginLeft: 16 }} />
            <SkeletonBlock pulse={pulse} height={12} width={80} style={{ marginLeft: 16 }} />
          </View>
        ))}
      </View>
    </ScrollView>
  </View>
);

const SkeletonKpiCard: React.FC<{ pulse: Animated.Value }> = ({ pulse }) => (
  <View style={{ flex: 1, backgroundColor: "#FFFFFF", borderRadius: 8, borderWidth: 1, borderColor: "#E1EAF5", padding: 16, marginRight: 12, minWidth: 150 }}>
    <SkeletonBlock pulse={pulse} height={11} width={60} style={{ marginBottom: 12 }} />
    <SkeletonBlock pulse={pulse} height={24} width={50} style={{ marginBottom: 8 }} />
    <SkeletonBlock pulse={pulse} height={10} width={80} />
  </View>
);

const SkeletonLoadingScreen: React.FC<{ pulse: Animated.Value }> = ({ pulse }) => (
  <View style={styles.screenRoot}>
    {/* Page Header */}
    <View style={styles.header}>
      <View style={styles.headerContent}>
        {/* Breadcrumb skeleton */}
        <SkeletonBlock pulse={pulse} height={14} width={200} style={{ marginBottom: 8 }} />
        {/* Title skeleton */}
        <SkeletonBlock pulse={pulse} height={28} width={300} style={{ marginBottom: 6 }} />
        {/* Subtitle skeleton */}
        <SkeletonBlock pulse={pulse} height={12} width="90%" />
      </View>
    </View>

    {/* Tabs skeleton */}
    <View style={{ flexDirection: "row", borderBottomWidth: 1, borderBottomColor: "#E8EDF5", backgroundColor: "#FFFFFF", paddingHorizontal: 16 }}>
      <SkeletonBlock pulse={pulse} height={16} width={80} style={{ marginRight: 24, paddingVertical: 12 }} />
      <SkeletonBlock pulse={pulse} height={16} width={100} style={{ marginRight: 24, paddingVertical: 12 }} />
      <SkeletonBlock pulse={pulse} height={16} width={90} style={{ paddingVertical: 12 }} />
    </View>

    {/* KPI Cards skeleton */}
    <View style={{ flexDirection: "row", padding: 16, backgroundColor: "#FFFFFF", gap: 8, marginBottom: 16 }}>
      <SkeletonKpiCard pulse={pulse} />
      <SkeletonKpiCard pulse={pulse} />
      <SkeletonKpiCard pulse={pulse} />
      <SkeletonKpiCard pulse={pulse} />
      <SkeletonKpiCard pulse={pulse} />
    </View>

    {/* Table skeleton */}
    <View style={{ paddingHorizontal: 16 }}>
      <SkeletonPoolTable pulse={pulse} />
    </View>
  </View>
);

const SparePoolScreen: React.FC<Props> = ({ onNavigate }) => {
  const { isSustainTeamMember, accessLoading } = useTicketingAccess();
  const toast = useToast();
  const dispatch = useDispatch<AppDispatch>();
  const authUser = useSelector((state: RootState) => state.auth.user);
  const sparePoolState = useSelector((state: RootState) => state.sparePool);
  const actorName = useMemo(() => {
    if (!authUser) return "Sustain Team";
    const email = authUser.email || authUser.username || "";
    return formatUserName(email) || "Sustain Team";
  }, [authUser]);
  
  // Skeleton animation
  const skeletonPulse = useRef(new Animated.Value(0.55)).current;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(skeletonPulse, { toValue: 1, duration: 700, useNativeDriver: true }),
        Animated.timing(skeletonPulse, { toValue: 0.55, duration: 700, useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [skeletonPulse]);
  
  // Derived pool units from API response
  const [pool, setPool] = useState<PoolUnit[]>([]);
  const [summary, setSummary] = useState({ availablePeak: 0, availableDematic: 0, reserved: 0, inMotion: 0, deployed: 0, inRepair: 0 });
  const [activeTab, setActiveTab] = useState<Tab>("pool");
  const [poolSearch, setPoolSearch] = useState("");
  const [deviceSearch, setDeviceSearch] = useState("");
  const [poolVendor, setPoolVendor] = useState("");
  const [poolType, setPoolType] = useState("");
  const [poolStatus, setPoolStatus] = useState("");
  const [poolPage, setPoolPage] = useState(1);
  const [poolPageSize, setPoolPageSize] = useState(50);
  const [devicePage, setDevicePage] = useState(1);
  const [devicePageSize, setDevicePageSize] = useState(50);
  const [expandedPoolId, setExpandedPoolId] = useState<string | null>(null);
  const [deviceType, setDeviceType] = useState("");
  const [deviceVendor, setDeviceVendor] = useState("");
  const [deviceCapture, setDeviceCapture] = useState("");
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const slideAnim = useRef(new Animated.Value(1)).current; // 1 = off-screen right, 0 = on-screen
  
  useEffect(() => {
    if (showDeviceModal) {
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 200,
        useNativeDriver: false,
      }).start();
    } else {
      Animated.timing(slideAnim, {
        toValue: 1,
        duration: 200,
        useNativeDriver: false,
      }).start();
    }
  }, [showDeviceModal, slideAnim]);
  const [editingDeviceId, setEditingDeviceId] = useState<string | null>(null);
  const [catalogDevices, setCatalogDevices] = useState<Device[]>(initialDevices);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState<string | null>(null);
  const [newSku, setNewSku] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newType, setNewType] = useState("");
  const [newGeneration, setNewGeneration] = useState("Gen 3");
  const [newVendor, setNewVendor] = useState("Peak Technologies");
  const [newCapture, setNewCapture] = useState<string>("Serial per unit");
  const [newPrefix, setNewPrefix] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [newActive, setNewActive] = useState(true);
  // WES devices can never be eligible for Voice to Vision Reclaim Return. A device is WES when it is
  // saved with Source Platform WES, or when "WES Device" is picked as the Equipment Type in the drawer.
  const isWesDevice =
    newType === WES_EQUIPMENT_TYPE ||
    catalogDevices.find((device) => device.id === editingDeviceId)?.sourcePlatform?.toUpperCase() === "WES";
  const [newEligibleRma, setNewEligibleRma] = useState(false);
  const [newEligibleNonRma, setNewEligibleNonRma] = useState(false);
  const [newEligibleReclaim, setNewEligibleReclaim] = useState(true);
  const [newProductPhoto, setNewProductPhoto] = useState<string | null>(null);
  const [newProductPhotoPreview, setNewProductPhotoPreview] = useState<string | null>(null);
  const [photoUploadProgress, setPhotoUploadProgress] = useState(0);
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);
  const [photoFileObject, setPhotoFileObject] = useState<File | null>(null);
  const [isPhotoLoading, setIsPhotoLoading] = useState(false);
  const photoLoadTokenRef = useRef(0);
  const [isSavingDevice, setIsSavingDevice] = useState(false);
  const drawerTranslateX = useRef(new Animated.Value(500)).current;
  const initialDeviceDraftKey = useRef("");
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState(false);
  const [backfillTickets, setBackfillTickets] = useState<BackfillTicket[]>(initialBackfillTickets);
  const [selectedBackfillId, setSelectedBackfillId] = useState(initialBackfillTickets[0].id);
  const [backfillSerials, setBackfillSerials] = useState("");
  const [backfillCarrier, setBackfillCarrier] = useState("FedEx");
  const [backfillTracking, setBackfillTracking] = useState("");
  const [backfillAccessories, setBackfillAccessories] = useState("");
  
  // Mobile responsive detection
  const windowWidth = Dimensions.get("window").width;
  const isMobile = Platform.OS === "ios" && windowWidth < 768;
  const [dataFetchError, setDataFetchError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // Map API response to pool units
  useEffect(() => {
    if (sparePoolState.units?.content) {
      const mappedUnits: PoolUnit[] = sparePoolState.units.content.map((unit: PoolUnitContent) => ({
        id: String(unit.poolId),
        vendor: unit.vendor || "",
        sku: unit.sku || "",
        description: unit.description || "",
        type: unit.equipmentType || "",
        serial: unit.serialNumber || "",
        quantity: unit.quantity || 1,
        location: unit.currentLocation || "",
        ticket: unit.ticketNumber || "",
        sentOut: unit.dateSentOut || "",
        received: unit.dateReceived || "",
        status: mapBackendStatusToPoolStatus(unit.status),
        history: (unit.history || []).map((h) => ({
          date: new Date(h.eventDate).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }),
          event: h.eventName || "",
          detail: h.detail || "",
          actor: h.actor || "",
          ticket: h.ticketNumber,
        })),
      }));
      setPool(mappedUnits);
    }
  }, [sparePoolState.units?.content]);

  // Map summary from Redux
  useEffect(() => {
    if (sparePoolState.summary) {
      setSummary(sparePoolState.summary);
    }
  }, [sparePoolState.summary]);

  // Handle data fetch errors
  useEffect(() => {
    if (sparePoolState.error) {
      setDataFetchError(sparePoolState.error);
    }
  }, [sparePoolState.error]);

  // Fetch data on component mount
  useEffect(() => {
    if (isSustainTeamMember) {
      dispatch(fetchSparePoolSummary());
      dispatch(
        fetchSparePoolUnits({
          page: 1,
          size: poolPageSize,
          sortBy: "vendor",
          sortDir: "asc",
          includeHistory: true,
        })
      );
    }
  }, [isSustainTeamMember, dispatch]);

  // Fetch pool units whenever filters, search, pagination, or page size changes
  useEffect(() => {
    if (isSustainTeamMember) {
      dispatch(
        fetchSparePoolUnits({
          page: poolPage,
          size: poolPageSize,
          sortBy: "vendor",
          sortDir: "asc",
          search: poolSearch || undefined,
          vendor: poolVendor || undefined,
          equipmentType: poolType || undefined,
          status: poolStatus || undefined,
          includeHistory: true,
        })
      );
    }
  }, [isSustainTeamMember, dispatch, poolPage, poolPageSize, poolSearch, poolVendor, poolType, poolStatus, retryCount]);

  const handleRetryFetch = () => {
    setDataFetchError(null);
    setRetryCount((prev) => prev + 1);
  };

  // Fetch devices from API on component mount
  useEffect(() => {
    const loadDevices = async () => {
      setDevicesLoading(true);
      setDevicesError(null);
      try {
        const response = await fetchDevices({ page: 0, size: 100 });
        if (response?.content && Array.isArray(response.content)) {
          setCatalogDevices(response.content);
        }
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : "Failed to load devices";
        setDevicesError(errorMsg);
        console.error("[SparePoolScreen] Failed to load devices:", err);
        // Fall back to initial devices on error
        setCatalogDevices(initialDevices);
      } finally {
        setDevicesLoading(false);
      }
    };

    loadDevices();
  }, []);

  // Use pool data directly from API response (no client-side filtering)
  const filteredPool = useMemo(() => {
    return pool;
  }, [pool]);

  const pagedPool = useMemo(() => {
    return pool;
  }, [pool]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPoolPage(1);
  }, [poolSearch, poolVendor, poolType, poolStatus]);

  const filteredDevices = useMemo(() => {
    const query = deviceSearch.trim().toLowerCase();
    return catalogDevices.filter((device) => {
      const matchesQuery = !query || [device.sku, device.description, device.type].some((value) => value.toLowerCase().includes(query));
      return matchesQuery && (!deviceType || device.type === deviceType) && (!deviceVendor || device.vendor === deviceVendor) && (!deviceCapture || device.capture === deviceCapture);
    });
  }, [catalogDevices, deviceCapture, deviceSearch, deviceType, deviceVendor]);

  const pagedDevices = useMemo(() => {
    const start = (devicePage - 1) * devicePageSize;
    return filteredDevices.slice(start, start + devicePageSize);
  }, [devicePage, devicePageSize, filteredDevices]);

  useEffect(() => {
    setDevicePage(1);
  }, [deviceSearch, deviceType, deviceVendor, deviceCapture]);

  // Conditional rendering - moved to JSX level to avoid hook rule violations
  if (accessLoading) return <LHLoader />;

  if (!isSustainTeamMember) {
    return (
      <View style={styles.centered}>
        <MaterialIcons name="lock-outline" size={30} color="#C12C01" />
        <Text style={styles.errorText}>You do not have permission to access Spare Pool and Device Management.</Text>
      </View>
    );
  }

  const resetPool = () => {
    setPoolSearch("");
    setPoolVendor("");
    setPoolType("");
    setPoolStatus("");
    setExpandedPoolId(null);
    setPoolPage(1);
  };

  const resetDevices = () => {
    setDeviceSearch("");
    setDeviceType("");
    setDeviceVendor("");
    setDeviceCapture("");
    setDevicePage(1);
  };

  const updatePoolStatus = async (id: string, newStatus: PoolStatus) => {
    const unit = pool.find((item) => item.id === id);
    if (!unit || unit.status === newStatus) return;
    
    const poolId = parseInt(id, 10);
    const actorEmail = authUser?.email || authUser?.username || "unknown@pepsico.com";
    const newLabel = poolStatusLabels[newStatus];

    // Call the API to update status
    const result = await dispatch(
      updateSparePoolUnitStatus(poolId, newLabel, actorEmail)
    );

    if (result) {
      toast.show(
        unit.ticket ? `Unit moved to ${newLabel}. Quantities on ${unit.ticket} updated.` : `Unit moved to ${newLabel}.`,
        "success",
        "Spare Pool"
      );
    } else {
      toast.show("Failed to update unit status. Please try again.", "error", "Spare Pool");
    }
  };

  const exportPoolRecords = () => {
    if (filteredPool.length > 10000) {
      toast.show("Export is being generated. You will be notified when it is ready.", "info", "Spare Pool");
      return;
    }
    const header = ["Vendor", "SKU", "Description", "Serial Number", "Quantity", "Status", "Current Location", "Ticket Number", "Date Sent Out", "Date Received"];
    const rows = filteredPool.map((unit) => [unit.vendor, unit.sku, unit.description, unit.serial || "", String(unit.quantity), poolStatusLabels[unit.status], unit.location, unit.ticket || "", unit.sentOut || "", unit.received || ""]);
    const csv = [header, ...rows].map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(",")).join("\n");
    if (Platform.OS === "web" && typeof document !== "undefined") {
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `spare-pool-export-${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.show("Spare pool export downloaded.", "success", "Spare Pool");
    } else {
      toast.show("Export is available on desktop.", "info", "Spare Pool");
    }
  };

  const openDeviceEditor = (device?: Device) => {
    const draft: DeviceDraft = {
      sku: device?.sku ?? "",
      description: device?.description ?? "",
      type: device?.type ?? "RF Device",
      generation: device?.generation ?? "A",
      vendor: device?.vendor ?? "Peak Technologies",
      capture: device?.capture ?? "Serial per unit",
      prefix: device?.prefix ?? "",
      notes: device?.notes ?? "",
      active: (device?.status ?? "Active") === "Active",
      // New devices default to Voice Pick: RMA / Non-RMA off, Reclaim on. Reclaim is never on for WES.
      eligibleRma: device?.eligibleRma ?? false,
      eligibleNonRma: device?.eligibleNonRma ?? false,
      eligibleReclaim: device?.sourcePlatform?.toUpperCase() === "WES" ? false : device?.eligibleReclaim ?? true,
      productPhoto: device?.productPhoto ?? null,
    };
    initialDeviceDraftKey.current = deviceDraftKey(draft);
    setEditingDeviceId(device?.id ?? null);
    setNewSku(draft.sku);
    setNewDescription(draft.description);
    setNewType(draft.type);
    setNewGeneration(draft.generation);
    setNewVendor(draft.vendor);
    setNewCapture(draft.capture);
    setNewPrefix(draft.prefix);
    setNewNotes(draft.notes);
    setNewActive(draft.active);
    setNewEligibleRma(draft.eligibleRma);
    setNewEligibleNonRma(draft.eligibleNonRma);
    setNewEligibleReclaim(draft.eligibleReclaim);
    setNewProductPhoto(draft.productPhoto ?? null);
    setNewProductPhotoPreview(draft.productPhoto ?? null);
    setShowDiscardConfirmation(false);
    setShowDeviceModal(true);

    // Edit flow: the list row may not carry the photo, so load the device record to show its current photo.
    const loadToken = ++photoLoadTokenRef.current;
    if (device?.id && !draft.productPhoto) {
      setIsPhotoLoading(true);
      getDeviceById(device.id)
        .then((detail) => {
          if (photoLoadTokenRef.current !== loadToken || !detail.productPhoto) return;
          setNewProductPhoto(detail.productPhoto);
          setNewProductPhotoPreview(detail.productPhoto);
          // Keep the loaded photo from counting as an unsaved change
          try {
            initialDeviceDraftKey.current = JSON.stringify({ ...JSON.parse(initialDeviceDraftKey.current), productPhoto: detail.productPhoto });
          } catch {
            /* leave the draft key unchanged */
          }
        })
        .catch((err) => console.error("[SparePoolScreen] Failed to load device photo:", err))
        .finally(() => {
          if (photoLoadTokenRef.current === loadToken) setIsPhotoLoading(false);
        });
    } else {
      setIsPhotoLoading(false);
    }
  };

  const closeDeviceEditor = () => {
    photoLoadTokenRef.current += 1;
    setIsPhotoLoading(false);
    setShowDeviceModal(false);
    setEditingDeviceId(null);
    setShowDiscardConfirmation(false);
    setNewProductPhoto(null);
    setNewProductPhotoPreview(null);
    setPhotoUploadProgress(0);
    setIsPhotoUploading(false);
    setPhotoFileObject(null);
  };

  const hasUnsavedDeviceChanges = () => deviceDraftKey({ sku: newSku, description: newDescription, type: newType, generation: newGeneration, vendor: newVendor, capture: newCapture, prefix: newPrefix, notes: newNotes, active: newActive, eligibleRma: newEligibleRma, eligibleNonRma: newEligibleNonRma, eligibleReclaim: newEligibleReclaim, productPhoto: newProductPhoto }) !== initialDeviceDraftKey.current;

  const requestCloseDeviceEditor = () => {
    if (hasUnsavedDeviceChanges()) {
      setShowDiscardConfirmation(true);
      return;
    }
    closeDeviceEditor();
  };

  const discardDeviceChanges = () => closeDeviceEditor();

  useEffect(() => {
    if (!showDeviceModal) return;
    drawerTranslateX.setValue(500);
    Animated.timing(drawerTranslateX, {
      toValue: 0,
      duration: 220,
      useNativeDriver: true,
    }).start();
  }, [drawerTranslateX, showDeviceModal]);

  const handlePhotoFileSelect = async (file: File | null) => {
    if (!file) {
      setNewProductPhoto(null);
      setNewProductPhotoPreview(null);
      setPhotoUploadProgress(0);
      setPhotoFileObject(null);
      return;
    }

    // One photo per device: the current photo must be removed before another can be added.
    if (newProductPhotoPreview || newProductPhoto || photoFileObject) {
      toast.show("This device already has a photo. Remove it before adding a new one.", "error", "Device Photo");
      return;
    }

    // Images only (JPG / PNG), max 10MB. Applies to click, drag-and-drop and paste.
    const isAllowedType = ["image/jpeg", "image/png"].includes(file.type) && /\.(jpe?g|png)$/i.test(file.name || "image.png");
    if (!isAllowedType) {
      toast.show("Only JPG and PNG images are allowed.", "error", "Device Photo");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.show("The image must be 10MB or smaller.", "error", "Device Photo");
      return;
    }

    // For Add mode (no device ID yet): Store file locally and create preview
    if (!editingDeviceId) {
      setPhotoFileObject(file);
      
      // Create preview immediately
      const reader = new FileReader();
      reader.onload = (e) => {
        setNewProductPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      
      toast.show("Photo selected. It will be uploaded when you save the device.", "info", "Device Photo");
      return;
    }

    // For Edit mode (has device ID): Upload immediately via API
    setIsPhotoUploading(true);
    setPhotoUploadProgress(0);

    try {
      const result = await uploadDevicePhoto(editingDeviceId, file, (progress) => {
        setPhotoUploadProgress(progress);
      });

      // Store the photo URL
      setNewProductPhoto(result.photoUrl);
      
      // Create a preview URL for display
      const reader = new FileReader();
      reader.onload = (e) => {
        setNewProductPhotoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      toast.show("Photo uploaded successfully", "success", "Device Photo");
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Failed to upload photo";
      toast.show(errorMsg, "error", "Device Photo");
      console.error("[SparePoolScreen] Photo upload failed:", err);
      setPhotoUploadProgress(0);
    } finally {
      setIsPhotoUploading(false);
    }
  };

  const saveDevice = async () => {
    const sku = (newSku ?? "").trim().toUpperCase();
    const description = (newDescription ?? "").trim();
    const type = (newType ?? "").trim();
    if (!sku || !description || !type) {
      toast.show("SKU, description, and equipment type are required.", "error", editingDeviceId ? "Edit Device" : "Add Device");
      return;
    }
    if (catalogDevices.some((device) => device.sku === sku && device.id !== editingDeviceId)) {
      toast.show(`SKU ${sku} already exists in the catalog.`, "error", "Device Catalog");
      return;
    }
    
    // Get submitter email
    const submitterEmail = authUser?.email || authUser?.username || "";
    if (!submitterEmail) {
      toast.show("Unable to determine user email. Please try again.", "error", "Device Catalog");
      return;
    }
    
    const editingDevice = catalogDevices.find((device) => device.id === editingDeviceId);

    // Extract generation number: "Gen 1" -> "1", "Gen 3" -> "3"
    const genValue = (newGeneration ?? "").replace(/Gen\s*/i, "").trim() || "3";
    
    const payload = {
      sku,
      description,
      type,
      generation: genValue,
      vendor: newVendor ?? "Peak Technologies",
      capture: newCapture ?? "Serial per unit",
      prefix: newCapture === "Serial per unit" ? (newPrefix ?? "").trim().toUpperCase() : "",
      notes: (newNotes ?? "").trim(),
      active: newActive,
      sourcePlatform: isWesDevice ? "WES" : editingDevice?.sourcePlatform ?? "VP",
      // Request paths where the device appears in the SKU picker; WES devices are never reclaim-eligible
      variantTypes: [
        ...(newEligibleRma ? ["RMA"] : []),
        ...(newEligibleNonRma ? ["NON_RMA"] : []),
        ...(newEligibleReclaim && !isWesDevice ? ["VOICE_TO_VISION"] : []),
      ],
      productPhoto: newProductPhoto ?? null,
      submitterEmail,
    };

    setIsSavingDevice(true);
    try {
      const wasEditing = Boolean(editingDeviceId);
      let createdDeviceId: string | null = null;
      
      if (wasEditing && editingDeviceId) {
        // Update existing device
        const updatedDevice = await updateDevice(editingDeviceId, payload);
        setCatalogDevices((current) =>
          current.map((device) => (device.id === editingDeviceId ? updatedDevice : device))
        );
        toast.show(`${description} updated.`, "success", "Device Catalog");
      } else {
        // Create new device
        const newDevice = await createDevice(payload);
        setCatalogDevices((current) => [...current, newDevice]);
        createdDeviceId = newDevice.id;
        
        // Upload photo for new device if file was selected
        if (photoFileObject && createdDeviceId) {
          try {
            setIsPhotoUploading(true);
            setPhotoUploadProgress(0);
            
            const result = await uploadDevicePhoto(createdDeviceId, photoFileObject, (progress) => {
              setPhotoUploadProgress(progress);
            });
            
            // Update device with photo URL in catalog
            setCatalogDevices((current) =>
              current.map((device) =>
                device.id === createdDeviceId
                  ? { ...device, productPhoto: result.photoUrl }
                  : device
              )
            );
            
            setIsPhotoUploading(false);
            toast.show(`${description} added to the catalog with photo.`, "success", "Device Catalog");
          } catch (err) {
            const errorMsg = err instanceof Error ? err.message : "Failed to upload photo";
            toast.show(`Device created, but photo upload failed: ${errorMsg}`, "warning", "Device Catalog");
            console.error("[SparePoolScreen] Photo upload failed:", err);
            setIsPhotoUploading(false);
          }
        } else {
          toast.show(`${description} added to the catalog.`, "success", "Device Catalog");
        }
      }
      
      closeDeviceEditor();
      setNewSku("");
      setNewDescription("");
      setNewType("");
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Failed to save device";
      toast.show(errorMsg, "error", "Device Catalog");
      console.error("[SparePoolScreen] Failed to save device:", err);
    } finally {
      setIsSavingDevice(false);
    }
  };

  const deactivateDevice = async () => {
    if (!editingDeviceId) return;
    const device = catalogDevices.find((item) => item.id === editingDeviceId);
    if (!device) return;

    try {
      const updatedDevice = await updateDeviceStatus(editingDeviceId, false);
      setCatalogDevices((current) =>
        current.map((item) => (item.id === editingDeviceId ? updatedDevice : item))
      );
      closeDeviceEditor();
      toast.show(`${device.description} deactivated. Its ${device.inPool} pool units remain tracked.`, "info", "Device Catalog");
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Failed to deactivate device";
      toast.show(errorMsg, "error", "Device Catalog");
      console.error("[SparePoolScreen] Failed to deactivate device:", err);
    }
  };

  const selectedBackfill = backfillTickets.find((ticket) => ticket.id === selectedBackfillId) ?? backfillTickets[0];

  const updateBackfill = (changes: Partial<BackfillTicket>) => {
    if (!selectedBackfill) return;
    setBackfillTickets((current) => current.map((ticket) => ticket.id === selectedBackfill.id ? { ...ticket, ...changes } : ticket));
  };

  const selectBackfillModel = (device: Device) => {
    if (!selectedBackfill) return;
    updateBackfill({ fulfillmentSku: device.sku, fulfillmentDevice: device.description, status: device.sku === selectedBackfill.requestedSku ? "sustain-review" : "site-accessories", accessories: [], serials: [], carrier: "", tracking: "" });
    setBackfillAccessories("");
    setBackfillSerials("");
    setBackfillTracking("");
  };

  const submitBackfillAccessories = () => {
    const accessories = backfillAccessories.split(",").map((item) => item.trim()).filter(Boolean);
    updateBackfill({ accessories, status: "sustain-accessory-review" });
    toast.show("Site requirements submitted to Sustain for validation.", "success", "Backfill Workbench");
  };

  const approveBackfillAccessories = () => {
    updateBackfill({ status: "vendor-fulfillment" });
    toast.show("Accessories validated and ticket forwarded to Peak.", "success", "Backfill Workbench");
  };

  const submitBackfillShipping = () => {
    const serials = backfillSerials.split(/[\n,]/).map((item) => item.trim()).filter(Boolean);
    if (!serials.length || !backfillTracking.trim()) {
      toast.show("Serial number and tracking are required.", "error", "Backfill Workbench");
      return;
    }
    updateBackfill({ serials, carrier: backfillCarrier, tracking: backfillTracking.trim(), status: "shipped", accessories: backfillAccessories.split(",").map((item) => item.trim()).filter(Boolean) });
    toast.show("Shipping details submitted. The ticket originator was notified.", "success", "Backfill Workbench");
  };

  // Handle loading state
  if (sparePoolState.loading && !pool.length) {
    return <SkeletonLoadingScreen pulse={skeletonPulse} />;
  }

  // Handle error state
  if (dataFetchError) {
    return (
      <View style={styles.centered}>
        <MaterialIcons name="error-outline" size={30} color="#C12C01" />
        <Text style={styles.errorText}>The spare pool could not be loaded. Retry.</Text>
        <Pressable onPress={handleRetryFetch} style={styles.primaryButton}>
          <Text style={styles.primaryButtonText}>Retry</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <View style={styles.screenRoot}>

      {/* Page Header */}
      <View style={styles.header}>
        <View style={styles.headerContent}>
          <View style={styles.breadcrumb}>
            <Pressable onPress={() => onNavigate?.("Ticketing Portal")}> 
              <Text style={styles.breadcrumbLink}>Ticketing Portal</Text>
            </Pressable>
            <Text style={styles.breadcrumbSep}>/</Text>
            <Text style={styles.breadcrumbCurrent}>Spare Pool and Device Management</Text>
          </View>
          <Text style={styles.pageTitle}>Spare Pool and Device Management</Text>
          <Text style={[styles.pageSubtitle, { marginTop: 4 }]}>PepsiCo Sustain owns the spare pool. Every unit is tracked from the moment it enters the pool to the moment it is deployed or retired.</Text>
        </View>
      </View>

      <View style={styles.tabs}>
        {(["pool", "device", "backfill", "report"] as Tab[]).map((tab) => (
          <Pressable key={tab} onPress={() => setActiveTab(tab)} style={[styles.tab, activeTab === tab && styles.tabActive]} accessibilityRole="tab" accessibilityState={{ selected: activeTab === tab }}>
            <Text style={[styles.tabText, activeTab === tab && styles.tabTextActive]}>{tab === "pool" ? "Spare Pool" : tab === "device" ? "Device Management" : tab === "backfill" ? "Backfill Workbench" : "Reconciliation Report"}</Text>
          </Pressable>
        ))}
      </View>

      <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer} horizontal={false}>
        {activeTab === "pool" ? (
          <>
            <View style={styles.kpiRow}>
              <Kpi label="Available in Pool" value={String(summary.availablePeak + summary.availableDematic)} detail="Peak and Dematic available stock" />
              <Kpi label="Reserved" value={String(summary.reserved)} detail="Committed to authorized tickets" />
              <Kpi label="In Motion" value={String(summary.inMotion)} detail="Inbound or out to a site" />
              <Kpi label="Deployed" value={String(summary.deployed)} detail="In service at a location" />
              <Kpi label="In Repair" value={String(summary.inRepair)} detail="Unavailable until QC passes" />
            </View>
            <View style={styles.tableCard}>
              <Toolbar search={poolSearch} onSearch={setPoolSearch} placeholder="Search serial number, SKU, location, or ticket" onReset={resetPool} onExport={exportPoolRecords}>
                <SingleSelectDropdown label="Vendor" value={poolVendor} emptyLabel="All vendors" options={unique(pool.map((unit) => unit.vendor))} onChange={setPoolVendor} />
                <SingleSelectDropdown label="Device Type" value={poolType} emptyLabel="All types" options={unique(pool.map((unit) => unit.type))} onChange={setPoolType} />
                <SingleSelectDropdown label="Status" value={poolStatus} emptyLabel="All statuses" options={Object.keys(poolStatusLabels)} optionLabels={poolStatusLabels} onChange={setPoolStatus} />
              </Toolbar>
              <TableScroll>
                <Row header poolHeader cells={["", "Vendor", "Device Type", "Serial Number", "Qty", "Current Location", "Ticket #", "Date Sent Out", "Date Received", "Status"]} />
                {pagedPool.map((unit) => <PoolRow key={unit.id} unit={unit} expanded={expandedPoolId === unit.id} onToggle={() => setExpandedPoolId(expandedPoolId === unit.id ? null : unit.id)} onStatusChange={(status) => updatePoolStatus(unit.id, status)} />)}
              </TableScroll>
              <View style={styles.tableFooterRow}>
                <Text style={styles.footerText}>{filteredPool.length} of {pool.length} pool records shown, {filteredPool.reduce((total, unit) => total + unit.quantity, 0)} of {pool.reduce((total, unit) => total + unit.quantity, 0)} units.</Text>
                <TablePagination page={poolPage} pageSize={poolPageSize} totalItems={filteredPool.length} onPageChange={setPoolPage} onPageSizeChange={(size) => { setPoolPageSize(size); setPoolPage(1); }} />
              </View>
            </View>
          </>
        ) : activeTab === "device" ? (
          isMobile ? (
            /* ── MOBILE VIEW: Device cards (read-only) ─────────────── */
            <>
              <View style={mobileStyles.infoBanner}>
                <MaterialIcons name="info-outline" size={16} color="#005CBC" />
                <Text style={mobileStyles.infoText}>Search and filter device records. Add and edit features are available on desktop.</Text>
              </View>

              <View style={mobileStyles.toolbarContainer}>
                <View style={mobileStyles.searchBox}>
                  <MaterialIcons name="search" size={18} color="#9099AA" />
                  <TextInput value={deviceSearch} onChangeText={setDeviceSearch} placeholder="Search SKU, description, or type" placeholderTextColor="#9099AA" style={mobileStyles.searchInput} accessibilityLabel="Search SKU, description, or equipment type" />
                </View>

                <View style={mobileStyles.filterRow}>
                  <View style={mobileStyles.filterDropdown}>
                    <SingleSelectDropdown label="" value={deviceType} emptyLabel="All types" options={unique(catalogDevices.map((device) => device.type))} onChange={setDeviceType} />
                  </View>
                  <View style={mobileStyles.filterDropdown}>
                    <SingleSelectDropdown label="" value={deviceVendor} emptyLabel="All vendors" options={unique(catalogDevices.map((device) => device.vendor))} onChange={setDeviceVendor} />
                  </View>
                </View>

                <View style={mobileStyles.filterRow}>
                  <View style={mobileStyles.filterDropdown}>
                    <SingleSelectDropdown label="" value={deviceCapture} emptyLabel="All" options={unique(catalogDevices.map((device) => device.capture))} onChange={setDeviceCapture} />
                  </View>
                  <Pressable onPress={resetDevices} style={mobileStyles.resetButton}>
                    <Text style={mobileStyles.resetText}>Reset</Text>
                  </Pressable>
                </View>
              </View>

              <ScrollView style={{ flex: 1 }} contentContainerStyle={mobileStyles.cardList}>
                {filteredDevices.length > 0 ? (
                  filteredDevices.map((device) => <MobileDeviceCard key={device.id} device={device} />)
                ) : (
                  <View style={mobileStyles.emptyState}>
                    <Text style={mobileStyles.emptyStateText}>No devices match your filters</Text>
                  </View>
                )}
              </ScrollView>

              <Text style={mobileStyles.footerText}>{filteredDevices.length} of {catalogDevices.length} devices shown</Text>
            </>
          ) : (
            /* ── DESKTOP VIEW: Device table (with edit/add) ─────────── */
            <>
              <View style={styles.infoBanner}>
                <MaterialIcons name="info-outline" size={18} color="#005CBC" />
                <Text style={styles.infoText}>This list contains the 50 Voice Pick catalog items from the updated workbook. Navigator tracks the spare pool. Voice Console remains the source for deployed network device location and full fleet tracking.</Text>
              </View>
              <View style={styles.tableCard}>
                <View style={styles.deviceToolbar}>
                  <View style={styles.searchBoxContainer}>
                    <View style={styles.searchBox}>
                      <MaterialIcons name="search" size={18} color="#9099AA" />
                      <TextInput value={deviceSearch} onChangeText={setDeviceSearch} placeholder="Search SKU, description, or equipment type" placeholderTextColor="#9099AA" style={styles.searchInput} accessibilityLabel="Search SKU, description, or equipment type" />
                    </View>
                  </View>
                  <View style={styles.filterGroupColumn}>
                    <Text style={styles.filterLabelInline}>EQUIPMENT TYPE</Text>
                    <SingleSelectDropdown label="" value={deviceType} emptyLabel="All types" options={unique(catalogDevices.map((device) => device.type))} onChange={setDeviceType} />
                  </View>
                  <View style={styles.filterGroupColumn}>
                    <Text style={styles.filterLabelInline}>ROUTES TO</Text>
                    <SingleSelectDropdown label="" value={deviceVendor} emptyLabel="All vendors" options={unique(catalogDevices.map((device) => device.vendor))} onChange={setDeviceVendor} />
                  </View>
                  <View style={styles.filterGroupColumn}>
                    <Text style={styles.filterLabelInline}>DATA CAPTURE</Text>
                    <SingleSelectDropdown label="" value={deviceCapture} emptyLabel="All" options={unique(catalogDevices.map((device) => device.capture))} onChange={setDeviceCapture} />
                  </View>
                  <View style={styles.filterActionButtons}>
                    <Pressable onPress={resetDevices} style={styles.resetButton}>
                      <Text style={styles.resetText}>Reset</Text>
                    </Pressable>
                    <Pressable style={styles.primaryButton} onPress={() => openDeviceEditor()} accessibilityRole="button">
                      <MaterialIcons name="add" size={17} color="#FFFFFF" />
                      <Text style={styles.primaryButtonText}>Add Device</Text>
                    </Pressable>
                  </View>
                </View>
                <TableScroll>
                  <DeviceHeader />
                  {pagedDevices.map((device) => <DeviceRow key={device.id} device={device} onEdit={() => openDeviceEditor(device)} />)}
                </TableScroll>
                <View style={styles.tableFooterRow}>
                  <Text style={styles.footerText}>{filteredDevices.length} of {catalogDevices.length} devices shown. {catalogDevices.filter((device) => device.capture === "Serial per unit").length} require a serial number, {catalogDevices.filter((device) => device.capture === "Quantity only").length} are quantity only.</Text>
                  <TablePagination page={devicePage} pageSize={devicePageSize} totalItems={filteredDevices.length} onPageChange={setDevicePage} onPageSizeChange={(size) => { setDevicePageSize(size); setDevicePage(1); }} />
                </View>
              </View>
            </>
          )
        ) : activeTab === "backfill" && selectedBackfill ? (
          <BackfillWorkbench
            ticket={selectedBackfill}
            tickets={backfillTickets}
            devices={catalogDevices.filter((device) => device.status === "Active")}
            onSelectTicket={setSelectedBackfillId}
            onSelectModel={selectBackfillModel}
            serials={backfillSerials}
            onSerialsChange={setBackfillSerials}
            carrier={backfillCarrier}
            onCarrierChange={setBackfillCarrier}
            tracking={backfillTracking}
            onTrackingChange={setBackfillTracking}
            accessories={backfillAccessories}
            onAccessoriesChange={setBackfillAccessories}
            onSubmitAccessories={submitBackfillAccessories}
            onApproveAccessories={approveBackfillAccessories}
            onSubmitShipping={submitBackfillShipping}
          />
        ) : activeTab === "backfill" ? <Text style={styles.emptyText}>No approved Non-RMA tickets.</Text> : <ReconciliationReport onExport={() => toast.show("Report exported.", "success", "Reconciliation Report")} />}
      </ScrollView>

      <Modal visible={showDeviceModal} transparent animationType="none" onRequestClose={requestCloseDeviceEditor}>
        <View style={styles.modalOverlay}>
          <Pressable style={styles.drawerBackdrop} onPress={requestCloseDeviceEditor} accessibilityLabel="Close device editor" />
          <Animated.View style={[styles.deviceDrawer, { transform: [{ translateX: drawerTranslateX }] }]}>
            <View style={styles.drawerHeader}>
              <View style={styles.drawerHeaderContent}>
                <Text style={styles.drawerEyebrow}>{editingDeviceId ? "EDIT DEVICE" : "NEW DEVICE"}</Text>
                <Text style={styles.drawerTitle}>{editingDeviceId ? "Edit device catalog record" : "Add a device to the catalog"}</Text>
              </View>
              <Pressable onPress={requestCloseDeviceEditor} style={styles.modalClose} accessibilityLabel="Close device editor">
                <MaterialIcons name="close" size={20} color="#818181" />
              </Pressable>
            </View>

            <ScrollView style={styles.drawerBody} contentContainerStyle={styles.drawerBodyContent} keyboardShouldPersistTaps="handled">
              <View style={styles.drawerSection}>
                <Text style={styles.drawerSectionTitle}>Identity</Text>
                <Field label="SKU" value={newSku} onChangeText={setNewSku} placeholder="e.g. LBDJ901291" helpText="Must be unique. Use the catalog SKU that appears in ticket and pool records." />
                <Field label="Description" value={newDescription} onChangeText={setNewDescription} placeholder="e.g. Talkman A720x wearable computer" />
                <View style={styles.drawerRow}>
                  <View style={styles.drawerRowMain}>
                    <SingleSelectDropdown label="Equipment Type" value={newType} options={EQUIPMENT_TYPES} allowEmpty={false} onChange={setNewType} />
                  </View>
                  <View style={styles.drawerRowSide}>
                    <SingleSelectDropdown label="Generation" value={newGeneration} options={unique(["A", "3", "1", "-"]) } allowEmpty={false} onChange={setNewGeneration} />
                  </View>
                </View>
              </View>

              <View style={styles.drawerSection}>
                <Text style={styles.drawerSectionTitle}>Ticket Eligibility</Text>
                {[
                  { key: "rma", label: "RMA Repair or Replacement", value: newEligibleRma, set: setNewEligibleRma, disabled: false },
                  { key: "nonrma", label: "Non-RMA Replacement (End of Life)", value: newEligibleNonRma, set: setNewEligibleNonRma, disabled: false, help: "Spare Pool Backfill uses this eligibility." },
                  { key: "reclaim", label: "Voice to Vision Reclaim Return", value: isWesDevice ? false : newEligibleReclaim, set: setNewEligibleReclaim, disabled: isWesDevice, help: isWesDevice ? "Reclaim returns cover Voice Pick equipment only." : undefined },
                ].map((item) => (
                  <Pressable
                    key={item.key}
                    style={[styles.toggleRow, item.disabled && { opacity: 0.5 }]}
                    onPress={() => !item.disabled && item.set((current: boolean) => !current)}
                    disabled={item.disabled}
                    accessibilityRole="switch"
                    accessibilityState={{ checked: item.value, disabled: item.disabled }}
                  >
                    <View style={styles.toggleCopy}>
                      <Text style={styles.toggleLabel}>{item.label}</Text>
                      {item.help ? <Text style={styles.toggleHelp}>{item.help}</Text> : null}
                    </View>
                    <View style={[styles.toggle, item.value && styles.toggleOn]}><View style={[styles.toggleThumb, item.value && styles.toggleThumbOn]} /></View>
                  </Pressable>
                ))}
                <Text style={styles.drawerHelp}>Controls which ticket paths show this device in the SKU picker. Changes apply to new tickets only; submitted tickets are not changed.</Text>
              </View>

              <View style={styles.drawerSection}>
                <Text style={styles.drawerSectionTitle}>Ticket Behavior</Text>
                <ChoiceField label="Data Capture" value={newCapture} options={["Serial per unit", "Quantity only"]} onChange={(value) => setNewCapture(value as CaptureMode)} />
                <Text style={styles.drawerHelp}>{newCapture === "Serial per unit" ? "Serial per unit means the ticket asks for one serial number for every unit." : "Quantity only means the ticket tracks the number of units without serial numbers."}</Text>
                <View style={styles.drawerFieldSpacing}>
                  <SingleSelectDropdown label="Routes To" value={newVendor} options={["Peak Technologies", "Dematic"]} allowEmpty={false} onChange={setNewVendor} />
                </View>
                <Text style={styles.drawerHelp}>Vendor destination is derived from this. Sites never choose a shipping address.</Text>
                {newCapture === "Serial per unit" && <View style={styles.drawerFieldSpacing}><Field label="Serial Prefix" value={newPrefix} onChangeText={setNewPrefix} placeholder="e.g. TKA" required={false} helpText="Used to match pasted serials to this device during bulk entry." /></View>}
              </View>

              <View style={styles.drawerSection}>
                <Text style={styles.drawerSectionTitle}>Availability</Text>
                <Pressable style={styles.toggleRow} onPress={() => setNewActive((active) => !active)} accessibilityRole="switch" accessibilityState={{ checked: newActive }}>
                  <View style={styles.toggleCopy}>
                    <Text style={styles.toggleLabel}>Active on new tickets</Text>
                    <Text style={styles.toggleHelp}>Turning this off hides the device from SKU dropdowns without affecting existing tickets or pool history.</Text>
                  </View>
                  <View style={[styles.toggle, newActive && styles.toggleOn]}><View style={[styles.toggleThumb, newActive && styles.toggleThumbOn]} /></View>
                </Pressable>
                <Field label="Notes" value={newNotes} onChangeText={setNewNotes} placeholder="Anything Warehouse Support should know" required={false} multiline />
              </View>

              <View style={styles.drawerSection}>
                <Text style={styles.drawerSectionTitle}>Product Photo</Text>
                <FileUploadField label="Device Product Photo" preview={newProductPhotoPreview} onFileSelect={handlePhotoFileSelect} acceptedFormats=".jpg, .jpeg, .png" maxSize="10MB" helpText="Upload a product photo for this device. Accepted formats: JPG, PNG. You can also drag an image onto the box or paste one from the clipboard." required={false} progress={photoUploadProgress} isUploading={isPhotoUploading} isLoading={isPhotoLoading} onValidationError={(message) => toast.show(message, "error", "Device Photo")} />
              </View>

              {editingDeviceId && <DevicePoolPosition device={catalogDevices.find((device) => device.id === editingDeviceId)} pool={pool} />}
            </ScrollView>

            <View style={styles.drawerFooter}>
              <Pressable style={styles.ghostButton} onPress={requestCloseDeviceEditor} disabled={isSavingDevice}><Text style={styles.ghostButtonText}>Cancel</Text></Pressable>
              {editingDeviceId && <Pressable style={styles.secondaryButton} onPress={deactivateDevice} disabled={isSavingDevice}><Text style={styles.secondaryButtonText}>Deactivate</Text></Pressable>}
              <Pressable 
                style={[styles.primaryButton, isSavingDevice && { opacity: 0.7 }]} 
                onPress={saveDevice}
                disabled={isSavingDevice}
              >
                {isSavingDevice ? (
                  <>
                    <LHLoader size={16} color="#FFFFFF" style={{ marginRight: 8 }} />
                    <Text style={styles.primaryButtonText}>Saving...</Text>
                  </>
                ) : (
                  <>
                    <MaterialIcons name="check" size={16} color="#FFFFFF" />
                    <Text style={styles.primaryButtonText}>Save Device</Text>
                  </>
                )}
              </Pressable>
            </View>
          </Animated.View>
        </View>
      </Modal>

      <Modal visible={showDiscardConfirmation} transparent animationType="fade" onRequestClose={() => setShowDiscardConfirmation(false)}>
        <View style={styles.confirmationOverlay}>
          <View style={styles.confirmationCard}>
            <Text style={styles.confirmationTitle}>Discard changes?</Text>
            <Text style={styles.confirmationMessage}>You have unsaved changes. Are you sure you want to discard them?</Text>
            <View style={styles.confirmationActions}>
              <Pressable style={styles.secondaryButton} onPress={() => setShowDiscardConfirmation(false)}><Text style={styles.secondaryButtonText}>Keep Editing</Text></Pressable>
              <Pressable style={styles.dangerButton} onPress={discardDeviceChanges}><Text style={styles.dangerButtonText}>Discard Changes</Text></Pressable>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
};

type BackfillWorkbenchProps = {
  ticket: BackfillTicket;
  tickets: BackfillTicket[];
  devices: Device[];
  onSelectTicket: (id: string) => void;
  onSelectModel: (device: Device) => void;
  serials: string;
  onSerialsChange: (value: string) => void;
  carrier: string;
  onCarrierChange: (value: string) => void;
  tracking: string;
  onTrackingChange: (value: string) => void;
  accessories: string;
  onAccessoriesChange: (value: string) => void;
  onSubmitAccessories: () => void;
  onApproveAccessories: () => void;
  onSubmitShipping: () => void;
};

const backfillStages: { key: BackfillStatus; label: string }[] = [
  { key: "sustain-review", label: "Approved Non-RMA" },
  { key: "site-accessories", label: "Site accessories" },
  { key: "sustain-accessory-review", label: "Sustain validation" },
  { key: "vendor-fulfillment", label: "Peak shipment" },
  { key: "shipped", label: "Shipped" },
];

const BackfillWorkbench: React.FC<BackfillWorkbenchProps> = ({ ticket, tickets, devices, onSelectTicket, onSelectModel, serials, onSerialsChange, carrier, onCarrierChange, tracking, onTrackingChange, accessories, onAccessoriesChange, onSubmitAccessories, onApproveAccessories, onSubmitShipping }) => {
  const selectedStage = backfillStages.findIndex((stage) => stage.key === ticket.status);
  const requestedDevice = devices.find((device) => device.sku === ticket.requestedSku);
  const selectedDevice = devices.find((device) => device.sku === ticket.fulfillmentSku);
  const ticketOptions = tickets.map((item) => item.id);
  const ticketLabels = tickets.reduce<Record<string, string>>((labels, item) => ({ ...labels, [item.id]: `${item.id} · ${item.location}` }), {});
  const deviceOptions = devices.map((device) => device.sku);
  const deviceLabels = devices.reduce<Record<string, string>>((labels, device) => ({ ...labels, [device.sku]: device.description }), {});

  return (
    <>
      <View style={styles.workbenchIntro}>
        <Text style={styles.workbenchTitle}>Spare Pool Backfill Workbench</Text>
        <Text style={styles.workbenchSubtitle}>Visible to Sustain and Peak. Sites only see cards when their action is required.</Text>
        <View style={styles.field}>
          <SingleSelectDropdown label="Approved Non-RMA ticket" value={ticket.id} options={ticketOptions} optionLabels={ticketLabels} allowEmpty={false} onChange={onSelectTicket} />
        </View>
      </View>

      <View style={styles.workbenchCard}>
        <View style={styles.backfillStage}>
          {backfillStages.map((stage, index) => <View key={stage.key} style={[styles.backfillStageItem, index <= selectedStage && styles.backfillStageItemActive]}><Text style={[styles.backfillStageText, index <= selectedStage && styles.backfillStageTextActive]}>{stage.label}</Text></View>)}
        </View>
        <View style={styles.workbenchGrid}>
          <ReadOnlyField label="Location" value={ticket.location} />
          <ReadOnlyField label="Originating Ticket" value={ticket.id} />
          <ReadOnlyField label="Requested Device" value={requestedDevice?.description ?? ticket.requestedDevice} />
          <ReadOnlyField label="Peak inventory available" value={`${ticket.available} units`} />
          <View style={styles.workbenchField}>
            <SingleSelectDropdown label="Fulfillment Device" value={ticket.fulfillmentSku} options={deviceOptions} optionLabels={deviceLabels} allowEmpty={false} onChange={(sku) => {
              const device = devices.find((item) => item.sku === sku);
              if (device) onSelectModel(device);
            }} />
            <Text style={styles.fieldHelp}>Use like for like when inventory is available.</Text>
          </View>
        </View>

        {ticket.fulfillmentSku === ticket.requestedSku ? <View style={styles.successNotice}><MaterialIcons name="check-circle-outline" size={17} color="#2C8C4A" /><Text style={styles.successNoticeText}>Like for like device selected.</Text></View> : <View style={styles.warningNotice}><MaterialIcons name="info-outline" size={17} color="#8B6508" /><Text style={styles.warningNoticeText}>A different model is selected. Confirm required headsets, ear pads, chargers, batteries, cables, belts, or holsters before forwarding the request.</Text></View>}

        {ticket.status === "site-accessories" && <View style={styles.actionPanel}><Text style={styles.actionTitle}>Waiting on the site to identify accessories</Text><Text style={styles.actionHelp}>Record the accessories and batteries needed with the substitute device, separated by commas.</Text><TextInput value={accessories} onChangeText={onAccessoriesChange} placeholder="e.g. A700 charger, spare battery" placeholderTextColor="#9099AA" style={styles.input} accessibilityLabel="Accessories and batteries needed" /><Pressable style={styles.primaryButton} onPress={onSubmitAccessories}><MaterialIcons name="send" size={16} color="#FFFFFF" /><Text style={styles.primaryButtonText}>Submit Site Requirements</Text></Pressable></View>}
        {ticket.status === "sustain-accessory-review" && <View style={styles.actionPanel}><Text style={styles.actionTitle}>Site requirements submitted</Text><Text style={styles.actionHelp}>Requested: {ticket.accessories.join(", ") || "No additional accessories"}</Text><Pressable style={styles.primaryButton} onPress={onApproveAccessories}><MaterialIcons name="check" size={16} color="#FFFFFF" /><Text style={styles.primaryButtonText}>Validate and Forward to Peak</Text></Pressable></View>}
        {ticket.status === "sustain-review" && <View style={styles.infoNotice}><MaterialIcons name="info-outline" size={17} color="#005CBC" /><Text style={styles.infoNoticeText}>Ready for Sustain review. A like-for-like selection can move directly to Peak fulfillment.</Text></View>}
        {ticket.status === "vendor-fulfillment" && <View style={styles.actionPanel}>
          <Text style={styles.actionTitle}>Peak fulfillment</Text>
          <Field label="Serial numbers being shipped" value={serials} onChangeText={onSerialsChange} placeholder="One serial per line" required={false} multiline />
          <View style={styles.field}><SingleSelectDropdown label="Carrier" value={carrier} options={SHIPMENT_CARRIERS} allowEmpty={false} onChange={onCarrierChange} /></View>
          <Field label="Tracking" value={tracking} onChangeText={onTrackingChange} placeholder="Enter tracking number" />
          <Field label="Accessories and batteries included" value={accessories} onChangeText={onAccessoriesChange} placeholder="Separate items with commas" required={false} />
          <Pressable style={styles.primaryButton} onPress={onSubmitShipping}><MaterialIcons name="local-shipping" size={16} color="#FFFFFF" /><Text style={styles.primaryButtonText}>Submit Shipping Details</Text></Pressable>
        </View>}
        {ticket.status === "shipped" && <View style={styles.successNotice}><MaterialIcons name="local-shipping" size={17} color="#2C8C4A" /><Text style={styles.successNoticeText}>{ticket.carrier} shipment submitted with tracking {ticket.tracking}.</Text></View>}
        {selectedDevice && <Text style={styles.workbenchMeta}>Fulfillment SKU {selectedDevice.sku} · {ticket.available} units currently available at Peak.</Text>}
      </View>
    </>
  );
};

const ReconciliationReport: React.FC<{ onExport: () => void }> = ({ onExport }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const expected = reconciliationRows.reduce((total, row) => total + row.expected, 0);
  const received = reconciliationRows.reduce((total, row) => total + row.received, 0);
  const damaged = reconciliationRows.reduce((total, row) => total + row.damaged, 0);
  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return reconciliationRows.slice(start, start + pageSize);
  }, [page, pageSize]);

  return (
    <>
      <View style={styles.kpiRow}>
        <Kpi label="Sites Reconciled" value={String(reconciliationRows.length)} detail="Completed shipment records" />
        <Kpi label="Expected Units" value={String(expected)} detail="Units listed on shipment records" />
        <Kpi label="Received Units" value={String(received)} detail="Units confirmed by the receiving site" />
        <Kpi label="Damaged or Variance" value={String(damaged)} detail="Units requiring follow-up" />
      </View>
      <View style={styles.tableCard}>
        <View style={styles.reportToolbar}>
          <Text style={styles.reportToolbarTitle}>Reconciled Inventory by Site and PBNA Network</Text>
          <Pressable style={[styles.secondaryButton, styles.reportExportButton]} onPress={onExport} accessibilityRole="button" accessibilityLabel="Export reconciliation report">
            <MaterialIcons name="file-download" size={16} color="#005CBC" />
            <Text style={styles.secondaryButtonText}>Export</Text>
          </Pressable>
        </View>
        <TableScroll>
          <View style={[styles.tableRow, styles.tableHeader]}>
            {(["Site", "Ticket", "Process", "Expected", "Received", "Damaged", "Status"] as string[]).map((heading, index) => <Text key={heading} style={[styles.tableCell, styles.tableHeaderText, index === 0 && styles.firstCell]}>{heading}</Text>)}
          </View>
          {pagedRows.map((row) => <View key={row.ticket} style={styles.tableRow}>
            <Text style={[styles.tableCell, styles.firstCell]}>{row.site}</Text>
            <Text style={[styles.tableCell, styles.ticketLink]}>{row.ticket}</Text>
            <Text style={styles.tableCell}>{row.process}</Text>
            <Text style={[styles.tableCell, styles.quantityCell]}>{String(row.expected)}</Text>
            <Text style={[styles.tableCell, styles.quantityCell]}>{String(row.received)}</Text>
            <Text style={[styles.tableCell, styles.quantityCell]}>{String(row.damaged)}</Text>
            <Text style={[styles.tableCell, row.status === "Closed" ? styles.reportStatusClosed : styles.reportStatusVariance]}>{row.status}</Text>
          </View>)}
        </TableScroll>
        <View style={styles.tableFooterRow}>
          <Text style={styles.footerText}>{reconciliationRows.length} of {reconciliationRows.length} reconciliation records shown.</Text>
          <TablePagination page={page} pageSize={pageSize} totalItems={reconciliationRows.length} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />
        </View>
      </View>
    </>
  );
};

const ReadOnlyField = ({ label, value }: { label: string; value: string }) => <View style={styles.workbenchField}><Text style={styles.fieldLabel}>{label}</Text><View style={styles.readOnlyField}><Text style={styles.readOnlyText}>{value}</Text></View></View>;

// Mobile-only Device Card Component for read-only view
const MobileDeviceCard = ({ device }: { device: Device }) => (
  <View style={mobileStyles.card}>
    <View style={mobileStyles.cardHeader}>
      <Text style={mobileStyles.cardTitle}>{device.sku}</Text>
      <Text style={mobileStyles.cardSubtitle}>{device.description}</Text>
    </View>
    
    <View style={mobileStyles.cardContent}>
      <View style={mobileStyles.cardField}>
        <Text style={mobileStyles.cardFieldLabel}>Equipment Type</Text>
        <Text style={mobileStyles.cardFieldValue}>{device.type}</Text>
      </View>

      <View style={mobileStyles.cardField}>
        <Text style={mobileStyles.cardFieldLabel}>Source Platform</Text>
        <View style={mobileStyles.vendorPill}>
          <Text style={mobileStyles.vendorPillText}>
            {device.vendor === "Peak Technologies" ? "PEAK" : device.vendor?.toUpperCase() || "VENDOR"}
          </Text>
        </View>
      </View>

      <View style={mobileStyles.cardField}>
        <Text style={mobileStyles.cardFieldLabel}>Data Capture</Text>
        <View style={[mobileStyles.capturePill, device.capture === "Quantity only" && mobileStyles.quantityCapturePill]}>
          <Text style={[mobileStyles.capturePillText, device.capture === "Quantity only" && mobileStyles.quantityCapturePillText]}>
            {device.capture?.toUpperCase() || "CAPTURE"}
          </Text>
        </View>
      </View>

      <View style={mobileStyles.cardField}>
        <Text style={mobileStyles.cardFieldLabel}>In Pool</Text>
        <Text style={mobileStyles.cardFieldValue}>{device.inPool}</Text>
      </View>

      <View style={mobileStyles.cardField}>
        <Text style={mobileStyles.cardFieldLabel}>Status</Text>
        <View style={[mobileStyles.statusPill, device.status === "Active" ? mobileStyles.statusPillActive : mobileStyles.statusPillInactive]}>
          <View style={[mobileStyles.statusDot, { backgroundColor: device.status === "Active" ? "#008A05" : "#9099AA" }]} />
          <Text style={[mobileStyles.statusText, device.status === "Active" ? mobileStyles.statusTextActive : mobileStyles.statusTextInactive]}>
            {device.status || "Inactive"}
          </Text>
        </View>
      </View>
    </View>

    <View style={mobileStyles.cardFooter}>
      <View style={mobileStyles.disabledEditButton}>
        <MaterialIcons name="lock-outline" size={16} style={mobileStyles.disabledEditIcon} />
        <Text style={mobileStyles.disabledEditText}>Device editing is available on desktop.</Text>
      </View>
    </View>
  </View>
);

const Kpi = ({ label, value, detail }: { label: string; value: string; detail: string }) => <View style={styles.kpi}><Text style={styles.kpiLabel}>{label}</Text><Text style={styles.kpiValue}>{value}</Text><Text style={styles.kpiDetail}>{detail}</Text></View>;

const DevicePoolPosition = ({ device, pool }: { device?: Device; pool: PoolUnit[] }) => {
  if (!device) return null;
  const matchingUnits = pool.filter((unit) => unit.sku === device.sku || unit.description === device.description);
  const inPool = matchingUnits.filter((unit) => unit.status === "in-pool").reduce((total, unit) => total + unit.quantity, 0);
  const inMotion = matchingUnits.filter((unit) => unit.status === "in-transit" || unit.status === "out-transit").reduce((total, unit) => total + unit.quantity, 0);
  const deployed = matchingUnits.filter((unit) => unit.status === "deployed").reduce((total, unit) => total + unit.quantity, 0);
  const hasPosition = inPool > 0 || inMotion > 0 || deployed > 0;

  return (
    <View style={styles.drawerSection}>
      <Text style={styles.drawerSectionTitle}>Spare Pool Position</Text>
      {hasPosition ? (
        <View style={styles.poolPositionGrid}>
          <PoolPositionCell label="In Pool" value={inPool} />
          <PoolPositionCell label="In Motion" value={inMotion} />
          <PoolPositionCell label="Deployed" value={deployed} />
        </View>
      ) : <Text style={styles.drawerMuted}>No spare pool records for this device yet.</Text>}
      <Text style={styles.drawerHelp}>Changing data capture on a device with pool history does not rewrite existing records.</Text>
    </View>
  );
};

const PoolPositionCell = ({ label, value }: { label: string; value: number }) => <View style={styles.poolPositionCell}><Text style={styles.poolPositionValue}>{String(value)}</Text><Text style={styles.poolPositionLabel}>{label}</Text></View>;

const Toolbar = ({ search, onSearch, placeholder, onReset, onExport, children }: { search: string; onSearch: (value: string) => void; placeholder: string; onReset: () => void; onExport?: () => void; children: React.ReactNode }) => (
  <View style={styles.toolbar}>
    <View style={styles.searchBox}>
      <MaterialIcons name="search" size={18} color="#9099AA" />
      <TextInput value={search} onChangeText={onSearch} placeholder={placeholder} placeholderTextColor="#9099AA" style={styles.searchInput} accessibilityLabel={placeholder} />
    </View>
    <View style={styles.filterRow}>
      {children}
      <Pressable onPress={onReset} style={styles.resetButton}><Text style={styles.resetText}>Reset</Text></Pressable>
    </View>
    {onExport && (
      <Pressable onPress={onExport} style={styles.exportButton} accessibilityRole="button" accessibilityLabel="Export">
        <MaterialIcons name="file-download" size={16} color="#005CBC" />
        <Text style={styles.exportButtonText}>Export</Text>
      </Pressable>
    )}
  </View>
);

const PAGE_SIZE_OPTIONS = ["50", "100", "200"];

const TablePagination = ({ page, pageSize, totalItems, onPageChange, onPageSizeChange }: { page: number; pageSize: number; totalItems: number; onPageChange: (page: number) => void; onPageSizeChange: (size: number) => void }) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(page, totalPages);
  return (
    <View style={styles.pagination}>
      <View style={styles.pageSizeGroup}>
        <Text style={styles.pageSizeLabel}>Rows per page:</Text>
        <View style={styles.pageSizeDropdown}>
          <SingleSelectDropdown label="" value={String(pageSize)} emptyLabel="Select" options={PAGE_SIZE_OPTIONS} allowEmpty={false} onChange={(val) => onPageSizeChange(Number(val))} />
        </View>
      </View>
      <View style={styles.pageNav}>
        <Pressable disabled={currentPage <= 1} onPress={() => onPageChange(currentPage - 1)} style={styles.pageNavButton} accessibilityRole="button" accessibilityLabel="Previous page">
          <MaterialIcons name="chevron-left" size={18} color={currentPage <= 1 ? "#C9D6E5" : "#005CBC"} />
        </Pressable>
        <Text style={styles.pageNavText}>Page {currentPage} of {totalPages}</Text>
        <Pressable disabled={currentPage >= totalPages} onPress={() => onPageChange(currentPage + 1)} style={styles.pageNavButton} accessibilityRole="button" accessibilityLabel="Next page">
          <MaterialIcons name="chevron-right" size={18} color={currentPage >= totalPages ? "#C9D6E5" : "#005CBC"} />
        </Pressable>
      </View>
    </View>
  );
};

const TableScroll = ({ children }: { children: React.ReactNode }) => <ScrollView horizontal showsHorizontalScrollIndicator contentContainerStyle={styles.tableScroll}><View style={styles.tableColumn}>{children}</View></ScrollView>;

const POOL_STATUS_OPTIONS = ["in-pool", "reserved", "out-transit", "deployed", "in-transit", "in-repair", "retired"] as PoolStatus[];

const PoolRow = ({ unit, expanded, onToggle, onStatusChange }: { unit: PoolUnit; expanded: boolean; onToggle: () => void; onStatusChange: (status: PoolStatus) => void }) => <>
  <Pressable onPress={onToggle} style={[styles.tableRow, expanded && styles.expandedRow]} accessibilityRole="button" accessibilityLabel={`Show history for ${unit.serial || unit.description}`}>
    <View style={styles.caretCell}><MaterialIcons name={expanded ? "keyboard-arrow-down" : "keyboard-arrow-right"} size={18} color={expanded ? "#005CBC" : "#B8C8DA"} /></View>
    <View style={[styles.tableCell, styles.firstCell]}><View style={styles.vendorPill}><Text style={styles.vendorPillText}>{unit.vendor === "Peak Technologies" ? "PEAK" : "DEMATIC"}</Text></View></View>
    <View style={styles.typeCell}><Text style={{ color: "#2D2D2D", fontSize: 12, fontWeight: "600", lineHeight: 16 }} numberOfLines={1}>{unit.type}</Text><Text style={[styles.subText]} numberOfLines={2}>{unit.description}</Text></View>
    <Text style={styles.tableCell}>{unit.serial || "Quantity tracked"}</Text>
    <Text style={[styles.tableCell, styles.quantityCell]}>{String(unit.quantity)}</Text>
    <Text style={styles.tableCell}>{unit.location}</Text>
    <Text style={[styles.tableCell, unit.ticket ? styles.ticketLink : styles.mutedCell]}>{unit.ticket || "None"}</Text>
    <Text style={[styles.tableCell, !unit.sentOut && styles.mutedCell]}>{unit.sentOut || "-"}</Text>
    <Text style={[styles.tableCell, !unit.received && styles.mutedCell]}>{unit.received || "-"}</Text>
    <View style={styles.statusCell}><View style={[styles.poolStatusPill, { backgroundColor: poolStatusBackground(unit.status) }]}><View style={[styles.statusDot, { backgroundColor: statusColor(unit.status) }]} /><Text style={[styles.statusText, { color: statusColor(unit.status) }]}>{poolStatusLabels[unit.status]}</Text></View></View>
  </Pressable>
  {expanded && <View style={styles.historyPanel}>
    <View style={styles.historyHeaderRow}>
      <View style={styles.historyHeaderText}>
        <Text style={styles.historyTitle}>History for {unit.serial || unit.description}</Text>
        <Text style={styles.historySub}>Pool ID {unit.id} · {unit.sku} · {poolStatusLabels[unit.status]}</Text>
      </View>
      {Platform.OS === "web" ? (
        <View style={styles.moveToControl}>
          <SingleSelectDropdown label="Move to" value={unit.status} options={POOL_STATUS_OPTIONS} optionLabels={poolStatusLabels} allowEmpty={false} onChange={(value) => onStatusChange(value as PoolStatus)} />
        </View>
      ) : <Text style={styles.moveToMobileNotice}>Pool status changes are available on desktop.</Text>}
    </View>
    <View style={styles.timeline}>{unit.history.slice().reverse().map((event, index) => <View key={`${event.date}-${event.event}-${index}`} style={styles.historyEvent}><View style={styles.timelineDot} /><View style={styles.historyBody}><View style={styles.historyTop}><Text style={styles.historyEventName}>{event.event}</Text><Text style={styles.historyDate}>{event.date}</Text></View><Text style={styles.historyDetail}>{event.detail}</Text><Text style={styles.historyMeta}>{event.actor}{event.ticket ? ` · ${event.ticket}` : ""}</Text></View></View>)}</View>
  </View>}
</>;

const Row = ({ cells, header, poolHeader, status }: { cells: string[]; header?: boolean; poolHeader?: boolean; status?: string }) => <View style={[styles.tableRow, header && styles.tableHeader]}>{cells.map((cell, index) => <Text key={`${cell}-${index}`} style={[styles.tableCell, header && styles.tableHeaderText, poolHeader && index === 0 && styles.poolHeaderCaret, index === (poolHeader ? 1 : 0) && styles.firstCell, status === "In Pool" && !header && styles.poolStatus, status === "Active" && !header && styles.activeStatus, status === "Inactive" && !header && styles.inactiveStatus]} numberOfLines={2}>{cell}</Text>)}</View>;

const DeviceRow = ({ device, onEdit }: { device: Device; onEdit: () => void }) => <View style={styles.tableRow}>
  <Text style={[styles.tableCell, styles.firstCell]} numberOfLines={2}>{device.sku}</Text>
  <View style={styles.deviceDescriptionCell}><Text style={[styles.tableCell, { paddingHorizontal: 0, paddingVertical: 0, color: "#333333", fontWeight: "500" }]} numberOfLines={2}>{device.description}</Text></View>
  <Text style={styles.tableCell}>{device.type}</Text>
  <Text style={styles.tableCell}>{device.generation}</Text>
  <View style={styles.tableCell}><View style={styles.vendorPill}><Text style={styles.vendorPillText}>{(device.vendor ?? "") === "Peak Technologies" ? "PEAK" : (device.vendor ?? "").toUpperCase()}</Text></View></View>
  <View style={styles.tableCell}><View style={[styles.capturePill, (device.capture ?? "") === "Quantity only" && styles.quantityCapturePill]}><Text style={[styles.capturePillText, (device.capture ?? "") === "Quantity only" && styles.quantityCapturePillText]}>{(device.capture ?? "").toUpperCase()}</Text></View></View>
  <Text style={[styles.tableCell, styles.quantityCell]}>{String(device.inPool)}</Text>
  <View style={styles.tableCell}><View style={[styles.deviceStatusPill, (device.status ?? "Inactive") === "Active" ? styles.deviceStatusActive : styles.deviceStatusInactive]}><View style={[styles.statusDot, { backgroundColor: (device.status ?? "Inactive") === "Active" ? "#008A05" : "#9099AA" }]} /><Text style={[styles.deviceStatusText, { color: (device.status ?? "Inactive") === "Active" ? "#008A05" : "#9099AA" }]}>{device.status ?? "Inactive"}</Text></View></View>
  <Pressable onPress={onEdit} style={styles.editButton} accessibilityRole="button" accessibilityLabel={`Edit ${device.description}`}><MaterialIcons name="edit" size={16} color="#005CBC" /><Text style={styles.editButtonText}>Edit</Text></Pressable>
</View>;

const DeviceHeader = () => <View style={[styles.tableRow, styles.tableHeader]}>
  <Text style={[styles.tableCell, styles.tableHeaderText, styles.firstCell]}>SKU</Text>
  <Text style={[styles.tableHeaderText, styles.deviceDescriptionCell]}>Description</Text>
  <Text style={[styles.tableCell, styles.tableHeaderText]}>Equipment Type</Text>
  <Text style={[styles.tableCell, styles.tableHeaderText]}>Gen</Text>
  <Text style={[styles.tableCell, styles.tableHeaderText]}>Routes To</Text>
  <Text style={[styles.tableCell, styles.tableHeaderText]}>Data Capture</Text>
  <Text style={[styles.tableCell, styles.tableHeaderText]}>In Pool</Text>
  <Text style={[styles.tableCell, styles.tableHeaderText, styles.statusCell]}>Status</Text>
  <View style={styles.editButton} />
</View>;

const statusColor = (status: PoolStatus) => ({ "in-pool": "#2C8C4A", reserved: "#7A71E0", "out-transit": "#0F87C5", deployed: "#725BC6", "in-transit": "#D0781D", "in-repair": "#8B6508", retired: "#9099AA" }[status]);
const poolStatusBackground = (status: PoolStatus) => ({ "in-pool": "#DDF6DE", reserved: "#EEEAFE", "out-transit": "#E4F4FB", deployed: "#EEEAFE", "in-transit": "#FFF0DF", "in-repair": "#FFF0B8", retired: "#EEF2F7" }[status]);

const ChoiceField = ({ label, value, options, onChange, helpText }: { label: string; value: string | undefined | null; options: string[]; onChange: (value: string) => void; helpText?: string }) => (
  <View style={styles.field}>
    <Text style={styles.fieldLabel}>
      {label}
      <Text style={styles.required}> *</Text>
    </Text>
    <View style={styles.choiceRow}>
      {options.map((option) => (
        <Pressable
          key={option}
          onPress={() => onChange(option)}
          style={[styles.choice, option === (value ?? "") && styles.choiceActive]}
        >
          <Text style={[styles.choiceText, option === (value ?? "") && styles.choiceTextActive]}>{option}</Text>
        </Pressable>
      ))}
    </View>
    {helpText && <Text style={styles.fieldHelp}>{helpText}</Text>}
  </View>
);

const Field = ({ label, value, onChangeText, placeholder, required = true, multiline = false, helpText }: { label: string; value: string | undefined | null; onChangeText: (value: string) => void; placeholder: string; required?: boolean; multiline?: boolean; helpText?: string }) => <View style={styles.field}><Text style={styles.fieldLabel}>{label}{required && <Text style={styles.required}> *</Text>}{!required && <Text style={styles.optional}> optional</Text>}</Text><TextInput value={String(value ?? "")} onChangeText={onChangeText} placeholder={placeholder} placeholderTextColor="#9099AA" style={[styles.input, multiline && styles.multilineInput]} multiline={multiline} />{helpText ? <Text style={styles.fieldHelp}>{helpText}</Text> : null}</View>;

const FileUploadField = ({ label, preview, onFileSelect, acceptedFormats = ".pdf, .doc, .docx, .ppt, .pptx", maxSize = "10MB", helpText, required = false, progress = 0, isUploading = false, isLoading = false, onValidationError }: { label: string; preview?: string | null; onFileSelect: (file: File | null) => void; acceptedFormats?: string; maxSize?: string; helpText?: string; required?: boolean; progress?: number; isUploading?: boolean; isLoading?: boolean; onValidationError?: (message: string) => void }) => {
  const dropZoneRef = useRef<any>(null);
  // The native file dialog opened by a click cannot be closed from code. If an image arrives by
  // drag/paste while it is still open, detach its handler so confirming it later does nothing.
  const activePickerRef = useRef<HTMLInputElement | null>(null);
  const dismissOpenPicker = () => {
    const picker = activePickerRef.current;
    if (picker) {
      picker.onchange = null;
      picker.value = "";
      activePickerRef.current = null;
    }
  };
  const dismissOpenPickerRef = useRef(dismissOpenPicker);
  dismissOpenPickerRef.current = dismissOpenPicker;
  const [isDragOver, setIsDragOver] = useState(false);
  const isUploadingRef = useRef(isUploading || isLoading);
  const onFileSelectRef = useRef(onFileSelect);
  const onValidationErrorRef = useRef(onValidationError);
  onValidationErrorRef.current = onValidationError;
  isUploadingRef.current = isUploading || isLoading;
  onFileSelectRef.current = onFileSelect;

  // Web only: drag-and-drop onto the box and paste (Ctrl/Cmd+V) of a copied image.
  // Type and size validation happens in the onFileSelect handler.
  useEffect(() => {
    if (Platform.OS !== "web") return undefined;
    const node = dropZoneRef.current as HTMLElement | null;
    if (!node?.addEventListener) return undefined;

    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      if (!isUploadingRef.current) setIsDragOver(true);
    };
    const onDragLeave = () => setIsDragOver(false);
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (isUploadingRef.current) return;
      const files = e.dataTransfer?.files;
      if (files && files.length > 1) {
        onValidationErrorRef.current?.("Add one image at a time.");
        return;
      }
      const file = files?.[0];
      if (file) {
        dismissOpenPickerRef.current();
        onFileSelectRef.current(file);
      }
    };
    const onPaste = (e: ClipboardEvent) => {
      if (isUploadingRef.current) return;
      const items = Array.from(e.clipboardData?.items ?? []);
      const imageItem = items.find((item) => item.kind === "file" && item.type.startsWith("image/"));
      const file = imageItem?.getAsFile();
      if (file) {
        e.preventDefault();
        dismissOpenPickerRef.current();
        onFileSelectRef.current(file);
      }
    };

    node.addEventListener("dragover", onDragOver);
    node.addEventListener("dragleave", onDragLeave);
    node.addEventListener("drop", onDrop);
    document.addEventListener("paste", onPaste);
    return () => {
      node.removeEventListener("dragover", onDragOver);
      node.removeEventListener("dragleave", onDragLeave);
      node.removeEventListener("drop", onDrop);
      document.removeEventListener("paste", onPaste);
    };
  }, []);

  return (
  <View style={styles.field}>
    <Text style={styles.fieldLabel}>{label}{required && <Text style={styles.required}> *</Text>}{!required && <Text style={styles.optional}> optional</Text>}</Text>
    <Pressable
      ref={dropZoneRef}
      onPress={() => {
        if (isUploading || isLoading) return; // Prevent multiple uploads / picking while the current photo loads
        if (preview) {
          onValidationError?.("This device already has a photo. Remove it before adding a new one.");
          return;
        }
        
        if (Platform.OS === "web") {
          const input = document.createElement("input");
          input.type = "file";
          input.accept = acceptedFormats;
          input.onchange = (e: any) => {
            const file = e.target.files?.[0];
            activePickerRef.current = null;
            if (file) {
              onFileSelect(file);
            }
          };
          activePickerRef.current = input;
          input.click();
        }
      }}
      style={[styles.fileUploadBox, isUploading && { opacity: 0.6 }, isDragOver && { borderColor: "#005CBC", backgroundColor: "#F0F6FF" }]}
      accessibilityRole="button"
      accessibilityLabel={`Upload ${label}`}
      disabled={isUploading || isLoading}
    >
      {isUploading ? (
        <View style={styles.fileUploadContent}>
          <MaterialIcons name="cloud-upload" size={32} color="#005CBC" />
          <Text style={styles.fileUploadTitle}>Uploading... {Math.round(progress * 100)}%</Text>
          <View style={{ width: "100%", maxWidth: 200, height: 4, backgroundColor: "#E1EAF5", borderRadius: 2, marginTop: 8, overflow: "hidden" }}>
            <View style={{ width: `${progress * 100}%`, height: "100%", backgroundColor: "#005CBC" }} />
          </View>
        </View>
      ) : isLoading ? (
        <View style={styles.fileUploadContent}>
          <LHLoader size={24} color="#005CBC" />
          <Text style={styles.fileUploadSubtext}>Loading current photo...</Text>
        </View>
      ) : preview ? (
        <View style={styles.fileUploadPreview}>
          <Image source={{ uri: preview }} style={{ width: 96, height: 96, borderRadius: 6, backgroundColor: "#FFFFFF" }} resizeMode="contain" accessibilityLabel="Device photo preview" />
          <Text style={styles.fileUploadPreviewText}>✓ Photo added</Text>
          <Pressable onPress={() => onFileSelect(null)} style={styles.fileUploadClear} accessibilityLabel="Remove photo">
            <Text style={styles.fileUploadClearText}>Remove</Text>
          </Pressable>
        </View>
      ) : (
        <View style={styles.fileUploadContent}>
          <MaterialIcons name="cloud-upload" size={32} color="#005CBC" />
          <Text style={styles.fileUploadTitle}>Click, drag and drop, or paste an image</Text>
          <Text style={styles.fileUploadSubtext}>{acceptedFormats} accepted • max {maxSize}</Text>
        </View>
      )}
    </Pressable>
    {helpText && <Text style={styles.fieldHelp}>{helpText}</Text>}
  </View>
  );
};

export default SparePoolScreen;